import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const fmtEuro = (n) => (Number(n) || 0).toLocaleString("it-IT", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const annoScolLabel = (a) => `${a}/${String((Number(a) || 0) + 1).slice(-2)}`;

export default function AgevolazioneModal({ alunnoCod, annoInitial, onClose, onSaved }) {
  const { user, annoCorrente } = useAuth();
  const isAdmin = user?.role === "admin";
  const [annoScol, setAnnoScol] = useState(Number(annoInitial) || Number(annoCorrente) || new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingStato, setChangingStato] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // blob URL per anteprima stampa
  const [printDialog, setPrintDialog] = useState(null); // { genitori, scelto, qual, motivo, loading }
  const [mailDialog, setMailDialog] = useState(null); // { loading, to, subject, body, sending, err, ok, alunno }

  const stato = data?.stato || "BOZZA";
  const isApprovata = stato === "APPROVATA";
  const isLocked = !isAdmin; // APPROVATA editabile con avviso (richiesta utente 2026-02)

  const carica = async (anno) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/agevolazioni/${alunnoCod}/${anno}`);
      const d = r.data;
      // Per le NUOVE schede (id=null) imposta attiva=true di default
      // (coerente con il vecchio comportamento di /crea-bozza).
      if (d && !d.id) {
        d.attiva = true;
      }
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento scheda");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carica(annoScol); }, [annoScol, alunnoCod]);

  const ricaricaDaListino = async () => {
    setErr(null);
    try {
      const r = await api.get(`/agevolazioni/${alunnoCod}/${annoScol}/listino-template`);
      const t = r.data;
      setData((d) => recalc({
        ...d,
        iscrizione: { ...t.iscrizione },
        rette: { ...t.rette },
        mensa: {
          ...(d.mensa || {}),
          settimane: t.mensa.settimane != null ? t.mensa.settimane : (d.mensa?.settimane || 0),
          mensa_listino: t.mensa.mensa_listino,
          mensa_agevolata: t.mensa.mensa_agevolata,
        },
      }));
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento listino");
    }
  };

  const upd = (path, value) => {
    setData((d) => {
      const next = { ...d };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      // Ricalcolo locale dei totali
      return recalc(next);
    });
  };

  const updVoce = (idx, key, value) => {
    setData((d) => {
      const arr = [...(d.altre_voci || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return recalc({ ...d, altre_voci: arr });
    });
  };

  // Quando l'utente digita direttamente nella colonna AGEVOLAZIONE,
  // calcolo l'accordato corrispondente per mantenere coerente la formula.
  const setAgevIscrizione = (val) => {
    setData((d) => {
      const listino = Number(d.iscrizione?.listino) || 0;
      const next = { ...d, iscrizione: { ...(d.iscrizione || {}), accordato: Math.max(0, listino - (Number(val) || 0)) } };
      return recalc(next);
    });
  };
  const setAgevRette = (val) => {
    setData((d) => {
      const r = d.rette || {};
      const totListino = (Number(r.nr_listino) || 0) * (Number(r.valore_listino) || 0);
      const nrAcc = Number(r.nr_accordate) || Number(r.nr_listino) || 0;
      const totAccordato = Math.max(0, totListino - (Number(val) || 0));
      const valAcc = nrAcc > 0 ? totAccordato / nrAcc : 0;
      const next = { ...d, rette: { ...r, nr_accordate: nrAcc, valore_accordato: Math.round(valAcc * 100) / 100 } };
      return recalc(next);
    });
  };
  const setAgevMensa = (val) => {
    setData((d) => {
      const m = d.mensa || {};
      const sett = Number(m.settimane) || 0;
      const pasti = Number(m.pasti_settimana) || 0;
      const listino = Number(m.mensa_listino) || 0;
      const denom = sett * pasti;
      const agevPerPasto = denom > 0 ? (Number(val) || 0) / denom : 0;
      const nuovaTar = Math.max(0, listino - agevPerPasto);
      const next = { ...d, mensa: { ...m, agevolata_attiva: true, mensa_agevolata: Math.round(nuovaTar * 100) / 100 } };
      return recalc(next);
    });
  };
  const setAgevVoce = (idx, val) => {
    setData((d) => {
      const arr = [...(d.altre_voci || [])];
      const v = arr[idx] || {};
      const listino = Number(v.listino) || 0;
      arr[idx] = { ...v, agevolato: Math.max(0, listino - (Number(val) || 0)) };
      return recalc({ ...d, altre_voci: arr });
    });
  };

  const recalc = (d) => {
    const isc = d.iscrizione || {};
    const r = d.rette || {};
    const m = d.mensa || {};
    const altre = d.altre_voci || [];
    const agev_iscrizione = (Number(isc.listino) || 0) - (Number(isc.accordato) || 0);
    const tot_rette_listino = (Number(r.nr_listino) || 0) * (Number(r.valore_listino) || 0);
    const tot_rette_accordato = (Number(r.nr_accordate) || 0) * (Number(r.valore_accordato) || 0);
    const agev_rette = tot_rette_listino - tot_rette_accordato;
    const sett = Number(m.settimane) || 0;
    const pasti = Number(m.pasti_settimana) || 0;
    const tot_mensa_listino = sett * pasti * (Number(m.mensa_listino) || 0);
    const tot_mensa_agevolato = m.agevolata_attiva
      ? sett * pasti * (Number(m.mensa_agevolata) || 0)
      : tot_mensa_listino;
    const agev_mensa = tot_mensa_listino - tot_mensa_agevolato;
    const agev_altre = altre.reduce((s, v) => s + ((Number(v.listino) || 0) - (Number(v.agevolato) || 0)), 0);
    // 🆕 Sconto fratelli: sottratto dal totale rette listino per calcolare la rata
    const sconto_fratelli = Number(d.sconto_fratelli) || 0;
    const tot_rette_agevolate = Math.max(0, tot_rette_listino - sconto_fratelli);
    // Rate 9 (live preview): 4 nell'anno scol + 5 nell'anno+1, totale = tot_rette_agevolate/9
    const annoS = Number(d.anno_scol) || 0;
    const rate_9 = [];
    if (annoS && tot_rette_agevolate > 0) {
      const schedule = [[annoS, 9], [annoS, 10], [annoS, 11], [annoS, 12],
        [annoS + 1, 1], [annoS + 1, 2], [annoS + 1, 3], [annoS + 1, 4], [annoS + 1, 5]];
      const base = Math.round((tot_rette_agevolate / 9) * 100) / 100;
      let acc = 0;
      schedule.forEach(([yy, mm], i) => {
        const imp = i === 8 ? Math.round((tot_rette_agevolate - acc) * 100) / 100 : base;
        acc += imp;
        rate_9.push({ nr: i + 1, data: `10/${String(mm).padStart(2, "0")}/${yy}`, importo: imp });
      });
    }
    return {
      ...d,
      calcoli: {
        agev_iscrizione, tot_rette_listino, tot_rette_accordato, agev_rette,
        tot_mensa_listino, tot_mensa_agevolato, agev_mensa, agev_altre,
        agev_totale: agev_iscrizione + agev_rette + agev_mensa + agev_altre,
        agev_netta: agev_iscrizione + agev_rette + agev_mensa + agev_altre - (Number(d.dote_scuola) || 0),
        sconto_fratelli,
        tot_rette_agevolate,
        rate_9,
      },
    };
  };

  const salva = async () => {
    setSaving(true);
    setErr(null);
    // 🆕 Validazione client: sconto_fratelli non può superare totale rette listino
    const scFrat = Number(data.sconto_fratelli) || 0;
    const totRetteList = (Number(data.rette?.nr_listino) || 0) * (Number(data.rette?.valore_listino) || 0);
    if (scFrat < 0) {
      setErr("Sconto fratelli non può essere negativo.");
      setSaving(false);
      return;
    }
    if (scFrat > totRetteList + 0.001) {
      setErr(`Sconto fratelli (€ ${fmtEuro(scFrat)}) non può superare il totale rette listino (€ ${fmtEuro(totRetteList)}).`);
      setSaving(false);
      return;
    }
    try {
      const payload = {
        alunno_cod: alunnoCod,
        anno_scol: annoScol,
        attiva: !!data.attiva,
        iscrizione: {
          listino: Number(data.iscrizione?.listino) || 0,
          accordato: Number(data.iscrizione?.accordato) || 0,
        },
        rette: {
          nr_listino: Number(data.rette?.nr_listino) || 0,
          valore_listino: Number(data.rette?.valore_listino) || 0,
          nr_accordate: Number(data.rette?.nr_accordate) || 0,
          valore_accordato: Number(data.rette?.valore_accordato) || 0,
        },
        mensa: {
          settimane: Number(data.mensa?.settimane) || 0,
          pasti_settimana: Number(data.mensa?.pasti_settimana) || 0,
          mensa_listino: Number(data.mensa?.mensa_listino) || 0,
          mensa_agevolata: Number(data.mensa?.mensa_agevolata) || 0,
          agevolata_attiva: !!data.mensa?.agevolata_attiva,
        },
        altre_voci: (data.altre_voci || []).map((v) => ({
          descrizione: v.descrizione || "",
          listino: Number(v.listino) || 0,
          agevolato: Number(v.agevolato) || 0,
        })),
        dote_scuola: Number(data.dote_scuola) || 0,
        note: data.note || "",
        rate_9_attiva: !!data.rate_9_attiva,
        sconto_fratelli: scFrat,
        pagamenti: {
          iscrizione: (data.pagamenti?.iscrizione || "").trim(),
          rette: (data.pagamenti?.rette || "").trim(),
        },
      };
      await api.put(`/agevolazioni/${alunnoCod}/${annoScol}`, payload);
      onSaved && onSaved({ active: payload.attiva, alunno_cod: alunnoCod, anno_scol: annoScol });
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const disattiva = async () => {
    if (!window.confirm("Disattivare la scheda agevolazione? I dati restano salvati ma il bottone tornerà neutro.")) return;
    setData((d) => ({ ...d, attiva: false }));
    setSaving(true);
    try {
      const payload = { ...data, attiva: false, alunno_cod: alunnoCod, anno_scol: annoScol };
      await api.put(`/agevolazioni/${alunnoCod}/${annoScol}`, payload);
      onSaved && onSaved({ active: false, alunno_cod: alunnoCod, anno_scol: annoScol });
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const cambiaStato = async (nuovoStato, prompt_note = false) => {
    if (!data?.id) {
      setErr("Salva prima la scheda per poter cambiare stato.");
      return;
    }
    let note = "";
    if (prompt_note) {
      note = window.prompt(`Motivazione per stato ${nuovoStato} (facoltativa):`, "") || "";
    } else if (nuovoStato === "APPROVATA") {
      if (!window.confirm(`Confermi APPROVAZIONE della scheda?\nDopo l'approvazione la scheda sarà BLOCCATA — solo la riapertura permetterà modifiche.`)) return;
    } else if (nuovoStato === "BOZZA") {
      if (!window.confirm(`Riaprire la scheda? Lo stato tornerà a BOZZA, perdendo eventuale approvazione/rifiuto.`)) return;
    }
    setChangingStato(true);
    setErr(null);
    try {
      const r = await api.post(`/agevolazioni/${alunnoCod}/${annoScol}/stato`, { stato: nuovoStato, note });
      setData(r.data);
      onSaved && onSaved({ active: r.data.attiva, alunno_cod: alunnoCod, anno_scol: annoScol, stato: r.data.stato });
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore cambio stato");
    } finally {
      setChangingStato(false);
    }
  };

  const statoColor = (s) => {
    switch (s) {
      case "APPROVATA":    return { bg: "#2e7d32", text: "white", border: "#1b5e20" };
      case "NO":           return { bg: "#c62828", text: "white", border: "#b71c1c" };
      case "USCITO":       return { bg: "#455a64", text: "white", border: "#37474f" };
      case "APPUNTAMENTO": return { bg: "#4527a0", text: "white", border: "#311b92" };
      case "RATE":         return { bg: "#0d47a1", text: "white", border: "#01579b" };
      default:             return { bg: "#0288d1", text: "white", border: "#01579b" };
    }
  };

  const c = data?.calcoli || {};
  const a = data?.alunno || {};

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 900, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 30, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="agevolazione-modal"
           style={{ background: "white", width: "100%", maxWidth: 1100, borderRadius: 6, overflow: "hidden" }}>
        {/* HEADER */}
        <div style={{ background: "linear-gradient(90deg,#1b5e20,#2e7d32)", color: "white", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>
              💰 SCHEDA AGEVOLAZIONE · {a.cognome} {a.nome}
              {data?.serial && (
                <span data-testid="agev-serial"
                  style={{ marginLeft: 10, padding: "2px 8px", background: "rgba(255,255,255,0.22)",
                    borderRadius: 3, fontFamily: "Consolas, monospace", fontSize: 12, fontWeight: 700,
                    letterSpacing: 0.5 }}
                  title="Numero seriale progressivo (prefisso A.S. + 8 cifre)">
                  #{data.serial}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              Cod: {alunnoCod}{a.cod_fis && <> · CF: <span style={{ fontFamily: "monospace" }}>{a.cod_fis}</span></>}
              {a.settore && <> · Settore: <b>{a.settore}</b></>}
              {a.classe && <> · Classe: <b>{a.classe}</b></>}
            </div>
          </div>
          <button onClick={onClose} data-testid="agev-close"
                  style={{ background: "none", border: "none", color: "white", fontSize: 26, cursor: "pointer" }}>✕</button>
        </div>

        {loading && <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento…</div>}
        {!loading && data && (
          <div style={{ padding: 18 }}>
            {/* SELETTORE ANNO + ATTIVA + STATO BADGE */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, padding: 10, background: "#f5f5f5", borderRadius: 4, flexWrap: "wrap" }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>ANNO SCOL.</label>
              <select value={annoScol} onChange={(e) => setAnnoScol(Number(e.target.value))}
                      data-testid="agev-anno"
                      disabled={!isAdmin}
                      style={{ padding: "6px 10px", fontSize: 13, fontWeight: 700, border: "2px solid #1565c0", borderRadius: 3 }}>
                {[-1, 0, 1, 2].map((off) => {
                  const y = (Number(annoCorrente) || new Date().getFullYear()) + off;
                  return <option key={y} value={y}>{annoScolLabel(y)}</option>;
                })}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700 }}>
                <input type="checkbox" checked={!!data.attiva} onChange={(e) => upd("attiva", e.target.checked)}
                       data-testid="agev-attiva" disabled={isLocked} />
                <span style={{ color: data.attiva ? "#1b5e20" : "#888" }}>
                  {data.attiva ? "✓ ATTIVA" : "Non attiva"}
                </span>
              </label>
              {(() => {
                const sc = statoColor(stato);
                return (
                  <span data-testid="agev-stato-badge"
                        style={{
                          background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                          padding: "4px 12px", fontSize: 12, fontWeight: 800, borderRadius: 3,
                          letterSpacing: 0.5,
                        }}>
                    {stato === "APPROVATA" ? "✓ " : ""}{stato}
                  </span>
                );
              })()}
              {data.listino_source ? (
                <span style={{ fontSize: 10, color: "#1565c0", fontStyle: "italic" }}>Listino {a.settore === "M" ? "SECONDARIA" : "PRIMARIA"} OK</span>
              ) : (
                <span style={{ fontSize: 10, color: "#c62828", fontStyle: "italic" }}>⚠ Listino mancante</span>
              )}
              {isAdmin && (
                <button onClick={ricaricaDaListino} disabled={loading}
                        data-testid="agev-reload-listino"
                        title="Sovrascrive iscrizione/rette/mensa con i valori del Listino corrente."
                        style={{ marginLeft: "auto", padding: "6px 12px", background: "#0288d1", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  🔄 Ricarica Listino
                </button>
              )}
            </div>

            {/* AVVISO MODIFICA SCHEDA APPROVATA */}
            {isApprovata && (
              <div data-testid="agev-warning-approvata"
                style={{
                  marginBottom: 14, padding: "10px 14px",
                  background: "#fff8e1", border: "2px solid #ef6c00", borderRadius: 4,
                  display: "flex", gap: 12, alignItems: "center",
                }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div style={{ flex: 1, fontSize: 12, lineHeight: 1.4, color: "#bf360c" }}>
                  <b style={{ fontSize: 13 }}>Attenzione — Scheda APPROVATA</b>
                  <br />
                  Stai modificando una scheda già approvata. Le modifiche verranno salvate e applicate immediatamente.
                  Se vuoi mantenere uno storico, considera di riportarla in <b>BOZZA</b> con il pulsante "🔓 RIAPRI" prima di modificare.
                </div>
              </div>
            )}

            {/* WORKFLOW BAR */}
            {isAdmin && data.id && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, padding: 8, background: "#fafafa", border: "1px dashed #bbb", borderRadius: 4, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>STATO:</span>
                {stato === "BOZZA" && (
                  <button onClick={() => cambiaStato("APPROVATA")} disabled={changingStato}
                          data-testid="agev-stato-approva" style={btnWorkflow("#2e7d32", "white")}>
                    ✓ APPROVA scheda
                  </button>
                )}
                {stato === "APPROVATA" && (
                  <>
                    <span style={{ fontSize: 11, color: "#1b5e20", fontWeight: 600 }}>
                      Approvata il <b>{data.approved_at ? new Date(data.approved_at).toLocaleString("it-IT") : "?"}</b> da <b>{data.approved_by || "?"}</b>
                    </span>
                    <button onClick={() => cambiaStato("BOZZA")} disabled={changingStato}
                            data-testid="agev-stato-riapri"
                            style={{ ...btnWorkflow("#ef6c00", "white"), marginLeft: "auto" }}>
                      🔓 RIAPRI (sblocca modifica)
                    </button>
                  </>
                )}
              </div>
            )}

            {err && <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, marginBottom: 10, fontSize: 12, borderRadius: 3 }}>{err}</div>}

            {/* TABELLA AGEVOLAZIONI */}
            <table style={tbl}>
              <thead>
                <tr style={{ background: "#37474f", color: "white" }}>
                  <th style={th}>VOCE</th>
                  <th style={thR}>LISTINO</th>
                  <th style={thR}>ACCORDATO</th>
                  <th style={{ ...thR, background: "#ef6c00" }}>AGEVOLAZIONE</th>
                </tr>
              </thead>
              <tbody>
                {/* 🆕 Calcolo % contribuzione da ISEE (formula: 34 + ISEE × 66/32000, clamp [34,100]) */}
                {(() => null)()}
                {/* ISCRIZIONE */}
                <tr>
                  <td style={tdLabel}>
                    📝 ISCRIZIONE
                    {(() => {
                      const iseeVal = data.alunno?.isee;
                      const listino = Number(data.iscrizione?.listino) || 0;
                      let pct = 0;
                      if (iseeVal != null && iseeVal !== "") {
                        const n = Number(iseeVal);
                        if (Number.isFinite(n)) {
                          pct = 34 + n * (100 - 34) / 32000;
                          if (pct < 34) pct = 34;
                          if (pct > 100) pct = 100;
                        }
                      }
                      const valConPct = (pct > 0) ? (listino * pct / 100) : listino;
                      return (
                        <div style={{ fontSize: 10, color: "#0d47a1", marginTop: 3, fontWeight: 600 }}
                          title={iseeVal != null ? `ISEE € ${Number(iseeVal).toLocaleString("it-IT")} → ${pct.toFixed(2)}% × € ${fmtEuro(listino)}` : "Nessun ISEE registrato → valore intero"}>
                          {pct > 0
                            ? <>Iscrizione × {pct.toFixed(2).replace(".", ",")}%: <b>€ {fmtEuro(valConPct)}</b></>
                            : <>Nessun ISEE → intero: <b>€ {fmtEuro(listino)}</b></>}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={tdR}>
                    <NumInput value={data.iscrizione?.listino} onChange={(v) => upd("iscrizione.listino", v)}
                              testid="agev-isc-listino" disabled={isLocked} />
                  </td>
                  <td style={tdR}>
                    <NumInput value={data.iscrizione?.accordato} onChange={(v) => upd("iscrizione.accordato", v)}
                              testid="agev-isc-accordato" disabled={isLocked} />
                  </td>
                  <td style={tdAgev}>
                    <NumInput value={c.agev_iscrizione} onChange={setAgevIscrizione}
                              testid="agev-isc-agevolazione" disabled={isLocked}
                              style={{ background: "#fff3e0", fontWeight: 800, color: "#bf360c", textAlign: "right" }} />
                  </td>
                </tr>

                {/* RETTE - LISTINO (display + edit) */}
                <tr style={{ background: "#fafafa" }}>
                  <td style={tdLabel}>
                    📚 RETTE — N° rate × valore (listino)
                    {(() => {
                      const iseeVal = data.alunno?.isee;
                      const nr = Number(data.rette?.nr_listino) || 0;
                      const val = Number(data.rette?.valore_listino) || 0;
                      const nrAgev = Number(data.rette?.nr_accordate) || 0;
                      const dote = Number(data.dote_scuola) || 0;
                      const totListino = nr * val;
                      // Calcolo % contribuzione da ISEE (34 + ISEE * 66/32000, clamp [34,100])
                      let pct = 0;
                      if (iseeVal != null && iseeVal !== "") {
                        const n = Number(iseeVal);
                        if (Number.isFinite(n)) {
                          pct = 34 + n * (100 - 34) / 32000;
                          if (pct < 34) pct = 34;
                          if (pct > 100) pct = 100;
                        }
                      }
                      // 🆕 Feb 2026 (richiesta utente): formula
                      // ((retta_listino_totale × %contrib) + dote) / nr_rate_agevolazione
                      const numeratore = totListino * pct / 100 + dote;
                      const perRata = nrAgev > 0 ? numeratore / nrAgev : 0;
                      return (
                        <div style={{ fontSize: 10, color: "#0d47a1", marginTop: 3, fontWeight: 600 }}
                          title={pct > 0
                            ? `((${nr}×${val} = € ${fmtEuro(totListino)}) × ${pct.toFixed(2)}%) + € ${fmtEuro(dote)} dote = € ${fmtEuro(numeratore)}${nrAgev > 0 ? ` / ${nrAgev} rate` : ""}`
                            : "Nessun ISEE registrato → nessuna % di contribuzione"}>
                          {pct > 0 && nrAgev > 0 ? (
                            <>
                              ((<b>{nr}×{val}</b> × <b>{pct.toFixed(2).replace(".", ",")}%</b>) + € <b>{fmtEuro(dote)}</b>) / <b>{nrAgev}</b> ={" "}
                              <b>€ {fmtEuro(perRata)}</b>/rata · Totale: <b>€ {fmtEuro(numeratore)}</b>
                            </>
                          ) : pct > 0 && nrAgev === 0 ? (
                            <>
                              ((<b>{nr}×{val}</b> × <b>{pct.toFixed(2).replace(".", ",")}%</b>) + € <b>{fmtEuro(dote)}</b>) = <b>€ {fmtEuro(numeratore)}</b>
                              <span style={{ color: "#bf360c", marginLeft: 6 }}>⚠ imposta N° rate agevolate</span>
                            </>
                          ) : (
                            <>Nessun ISEE → nessuna % di contribuzione applicabile</>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={tdR}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                      <NumInput value={data.rette?.nr_listino} onChange={(v) => upd("rette.nr_listino", v)}
                                style={{ width: 50 }} testid="agev-rette-nr-listino" disabled={isLocked} />
                      <span>×</span>
                      <NumInput value={data.rette?.valore_listino} onChange={(v) => upd("rette.valore_listino", v)}
                                style={{ width: 90 }} testid="agev-rette-val-listino" disabled={isLocked} />
                    </div>
                    <div style={{ fontSize: 10, color: "#888", marginTop: 2, textAlign: "right" }}>
                      Tot. listino: <b>€ {fmtEuro(c.tot_rette_listino)}</b>
                    </div>
                  </td>
                  <td style={tdR}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                      <NumInput value={data.rette?.nr_accordate} onChange={(v) => upd("rette.nr_accordate", v)}
                                style={{ width: 50 }} testid="agev-rette-nr-acc" disabled={isLocked} />
                      <span>×</span>
                      <NumInput value={data.rette?.valore_accordato} onChange={(v) => upd("rette.valore_accordato", v)}
                                style={{ width: 90 }} testid="agev-rette-val-acc" disabled={isLocked} />
                    </div>
                    <div style={{ fontSize: 10, color: "#888", marginTop: 2, textAlign: "right" }}>
                      Tot. accordato: <b>€ {fmtEuro(c.tot_rette_accordato)}</b>
                    </div>
                  </td>
                  <td style={tdAgev}>
                    <NumInput value={c.agev_rette} onChange={setAgevRette}
                              testid="agev-rette-agevolazione" disabled={isLocked}
                              style={{ background: "#fff3e0", fontWeight: 800, color: "#bf360c", textAlign: "right" }} />
                  </td>
                </tr>

                {/* MENSA */}
                <tr>
                  <td style={tdLabel}>
                    🍽 MENSA
                    <div style={{ fontSize: 10, color: "#666", fontWeight: 400 }}>
                      Settimane × Pasti/sett × €/pasto
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, fontWeight: 700, color: data.mensa?.agevolata_attiva ? "#1b5e20" : "#888" }}>
                      <input type="checkbox" checked={!!data.mensa?.agevolata_attiva}
                             onChange={(e) => upd("mensa.agevolata_attiva", e.target.checked)}
                             data-testid="agev-mensa-attiva" disabled={isLocked} />
                      Applica tariffa mensa AGEVOLATA
                    </label>
                  </td>
                  <td style={tdR}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center", fontSize: 11 }}>
                      <NumInput value={data.mensa?.settimane} onChange={(v) => upd("mensa.settimane", v)}
                                style={{ width: 45 }} testid="agev-mensa-sett" disabled={isLocked} />
                      <span>×</span>
                      <NumInput value={data.mensa?.pasti_settimana} onChange={(v) => upd("mensa.pasti_settimana", v)}
                                style={{ width: 35 }} testid="agev-mensa-pasti" disabled={isLocked} />
                      <span>×</span>
                      <NumInput value={data.mensa?.mensa_listino} onChange={(v) => upd("mensa.mensa_listino", v)}
                                style={{ width: 65 }} testid="agev-mensa-list" disabled={isLocked} />
                    </div>
                    <div style={{ fontSize: 10, color: "#888", marginTop: 2, textAlign: "right" }}>
                      Tot. listino: <b>€ {fmtEuro(c.tot_mensa_listino)}</b>
                    </div>
                  </td>
                  <td style={tdR}>
                    {data.mensa?.agevolata_attiva ? (
                      <>
                        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center", fontSize: 11 }}>
                          <span style={{ color: "#888" }}>{data.mensa?.settimane || 0} × {data.mensa?.pasti_settimana || 0} ×</span>
                          <NumInput value={data.mensa?.mensa_agevolata} onChange={(v) => upd("mensa.mensa_agevolata", v)}
                                    style={{ width: 65 }} testid="agev-mensa-agev" disabled={isLocked} />
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 2, textAlign: "right" }}>
                          Tot. agevolato: <b>€ {fmtEuro(c.tot_mensa_agevolato)}</b>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, color: "#999", textAlign: "right", fontStyle: "italic" }}>
                        Tariffa agevolata non attiva
                        <div style={{ fontSize: 10, marginTop: 2 }}>
                          Tot. = listino: <b>€ {fmtEuro(c.tot_mensa_agevolato)}</b>
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={tdAgev}>
                    <NumInput value={c.agev_mensa} onChange={setAgevMensa}
                              testid="agev-mensa-agevolazione" disabled={isLocked || !data.mensa?.agevolata_attiva}
                              style={{ background: "#fff3e0", fontWeight: 800, color: "#bf360c", textAlign: "right" }} />
                  </td>
                </tr>

                {/* ALTRE VOCI (3 righe) */}
                {(data.altre_voci || []).map((v, idx) => (
                  <tr key={idx} style={{ background: "#fafafa" }}>
                    <td style={tdLabel}>
                      <input value={v.descrizione || ""} onChange={(e) => updVoce(idx, "descrizione", e.target.value)}
                             placeholder={`Altra voce ${idx + 1}`}
                             data-testid={`agev-voce-desc-${idx}`}
                             disabled={isLocked}
                             style={{ width: "100%", padding: "4px 6px", border: "1px solid #ccc", borderRadius: 2, fontSize: 11 }} />
                    </td>
                    <td style={tdR}>
                      <NumInput value={v.listino} onChange={(val) => updVoce(idx, "listino", val)}
                                testid={`agev-voce-list-${idx}`} disabled={isLocked} />
                    </td>
                    <td style={tdR}>
                      <NumInput value={v.agevolato} onChange={(val) => updVoce(idx, "agevolato", val)}
                                testid={`agev-voce-agev-${idx}`} disabled={isLocked} />
                    </td>
                    <td style={tdAgev}>
                      <NumInput value={(Number(v.listino) || 0) - (Number(v.agevolato) || 0)}
                                onChange={(val) => setAgevVoce(idx, val)}
                                testid={`agev-voce-agevolazione-${idx}`} disabled={isLocked}
                                style={{ background: "#fff3e0", fontWeight: 800, color: "#bf360c", textAlign: "right" }} />
                    </td>
                  </tr>
                ))}

                {/* TOTALE LORDO */}
                <tr style={{ background: "#fff59d", borderTop: "3px solid #fbc02d" }}>
                  <td colSpan={3} style={{ ...tdLabel, textAlign: "right", fontSize: 13, fontWeight: 800 }}>
                    TOTALE AGEVOLAZIONE LORDA
                  </td>
                  <td style={{ ...tdAgev, fontSize: 15, color: "#bf360c" }}>€ {fmtEuro(c.agev_totale)}</td>
                </tr>
                {/* DOTE SCUOLA (sottratta) */}
                <tr style={{ background: "#e3f2fd" }}>
                  <td colSpan={3} style={{ ...tdLabel, textAlign: "right", fontSize: 12, fontWeight: 700, color: "#0d47a1" }}>
                    − DOTE SCUOLA ANNUALE (a carico ente esterno)
                  </td>
                  <td style={{ ...tdAgev, fontSize: 13, color: "#0d47a1", background: "#e3f2fd" }}>− € {fmtEuro(data.dote_scuola)}</td>
                </tr>
                {/* TOTALE NETTO */}
                <tr style={{ background: "#c8e6c9", borderTop: "3px solid #2e7d32" }}>
                  <td colSpan={3} style={{ ...tdLabel, textAlign: "right", fontSize: 14, fontWeight: 800, color: "#1b5e20" }}>
                    AGEVOLAZIONE NETTA (a carico scuola)
                  </td>
                  <td style={{ ...tdAgev, fontSize: 18, color: "#1b5e20", background: "#c8e6c9" }}>€ {fmtEuro(c.agev_netta)}</td>
                </tr>
              </tbody>
            </table>

            {/* RATEIZZAZIONE IN 9 RATE — opzione di pagamento sul listino (feb 2026) */}
            <div data-testid="agev-rate-9-section" style={{ marginTop: 14, border: "1px solid #0d47a1", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ background: "#0d47a1", color: "white", padding: "6px 10px", fontSize: 12, fontWeight: 800, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: isLocked ? "not-allowed" : "pointer" }}>
                  <input type="checkbox" checked={!!data.rate_9_attiva}
                         onChange={(e) => upd("rate_9_attiva", e.target.checked)}
                         data-testid="agev-rate-9-toggle" disabled={isLocked} />
                  <span>📆 RATEIZZAZIONE IN 9 RATE</span>
                </label>
                {data.rate_9_attiva && Array.isArray(c.rate_9) && c.rate_9.length > 0 && (
                  <span style={{ fontWeight: 600, opacity: 0.95 }}>
                    — totale € {fmtEuro(c.rate_9.reduce((s, r) => s + (Number(r.importo) || 0), 0))}
                  </span>
                )}
                {!data.rate_9_attiva && (
                  <span style={{ fontWeight: 500, opacity: 0.85, fontSize: 11 }}>
                    (opzione di pagamento sulle quote listino — disattivata)
                  </span>
                )}
              </div>
              {/* 🆕 SCONTO FRATELLI — sempre visibile, riduce il totale usato per la rateizzazione */}
              <div style={{ background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", padding: "8px 10px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#37474f", display: "flex", alignItems: "center", gap: 6 }}>
                  👨‍👩‍👧‍👦 SCONTO FRATELLI €
                </label>
                <NumInput
                  value={data.sconto_fratelli}
                  onChange={(v) => upd("sconto_fratelli", v)}
                  testid="agev-sconto-fratelli"
                  disabled={isLocked}
                  style={{ width: 110, background: "#fff8e1", fontWeight: 700, color: "#bf360c" }}
                />
                <span style={{ fontSize: 11, color: "#555", fontFamily: "Consolas, monospace" }}>
                  Retta listino: <b>€ {fmtEuro(c.tot_rette_listino)}</b>
                  {" − "}
                  Sconto: <b style={{ color: "#c62828" }}>€ {fmtEuro(c.sconto_fratelli)}</b>
                  {" = "}
                  <span style={{ padding: "2px 8px", background: "#c8e6c9", border: "1px solid #2e7d32", borderRadius: 3 }}>
                    Retta agevolata: <b style={{ color: "#1b5e20" }}>€ {fmtEuro(c.tot_rette_agevolate)}</b>
                  </span>
                </span>
                {(Number(data.sconto_fratelli) || 0) > (Number(c.tot_rette_listino) || 0) + 0.001 && (
                  <span data-testid="agev-sconto-fratelli-error"
                        style={{ color: "#b71c1c", fontSize: 11, fontWeight: 700 }}>
                    ⚠ Lo sconto non può superare il totale rette listino
                  </span>
                )}
              </div>
              {data.rate_9_attiva && Array.isArray(c.rate_9) && c.rate_9.length > 0 && (
                <table data-testid="agev-rate-9" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      {c.rate_9.map((r, i) => (
                        <td key={`d-${i}`} style={{ border: "1px solid #ccc", padding: "3px 4px", textAlign: "center", fontSize: 10, background: "#e3f2fd", color: "#0d47a1" }}>
                          {r.data}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      {c.rate_9.map((r, i) => (
                        <td key={`i-${i}`} style={{ border: "1px solid #ccc", padding: "4px 4px", textAlign: "center", fontSize: 13, fontWeight: 700, color: "#0d47a1" }}>
                          € {fmtEuro(r.importo)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* MODI E TEMPI PAGAMENTO */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <div>
                <label style={lbl}>💳 MODI E TEMPI PAGAMENTO ISCRIZIONE</label>
                <textarea value={data.pagamenti?.iscrizione || ""} onChange={(e) => upd("pagamenti.iscrizione", e.target.value)}
                          data-testid="agev-pag-isc" disabled={isLocked} rows={2}
                          placeholder="Es. bonifico unico entro 30/06"
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
              </div>
              <div>
                <label style={lbl}>💳 MODI E TEMPI PAGAMENTO RETTE</label>
                <textarea value={data.pagamenti?.rette || ""} onChange={(e) => upd("pagamenti.rette", e.target.value)}
                          data-testid="agev-pag-rette" disabled={isLocked} rows={2}
                          placeholder="Es. 10 rate mensili da settembre a giugno"
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
              </div>
            </div>

            {/* DOTE SCUOLA + NOTE */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginTop: 14 }}>
              <div>
                <label style={lbl}>🎓 DOTE SCUOLA ANNUALE €</label>
                <NumInput value={data.dote_scuola} onChange={(v) => upd("dote_scuola", v)}
                          testid="agev-dote" disabled={isLocked}
                          style={{ width: "100%", textAlign: "right", fontSize: 14, fontWeight: 700 }} />
              </div>
              <div>
                <label style={lbl}>📝 NOTE</label>
                <textarea value={data.note || ""} onChange={(e) => upd("note", e.target.value)}
                          data-testid="agev-note" disabled={isLocked}
                          rows={2}
                          style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
              </div>
            </div>

            {data.updated_at && (
              <div style={{ marginTop: 12, fontSize: 10, color: "#999", textAlign: "right" }}>
                Aggiornato il {new Date(data.updated_at).toLocaleString("it-IT")} da <b>{data.updated_by || "?"}</b>
              </div>
            )}
          </div>
        )}

        {/* FOOTER */}
        <div style={{ borderTop: "1px solid #eee", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafafa" }}>
          <div>
            {data?.id && isAdmin && (
              <button onClick={disattiva} disabled={saving} data-testid="agev-disattiva"
                      style={{ padding: "8px 14px", background: "white", border: "1px solid #c62828", color: "#c62828", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                ⊘ Disattiva scheda
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} data-testid="agev-annulla"
                    style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              Chiudi
            </button>
            <button onClick={async () => {
                  if (!data?.id) { alert("Salva prima la scheda per poterla stampare."); return; }
                  setPrintDialog({ loading: true, genitori: [], scelto: null, qual: "", motivo: "" });
                  try {
                    const r = await api.get(`/agevolazioni/${alunnoCod}/${annoScol}/genitori`);
                    const genitori = r.data?.data || [];
                    setPrintDialog({ loading: false, genitori, scelto: null, qual: genitori[0]?.qualifica || "GENITORE", motivo: "" });
                  } catch (e) {
                    setPrintDialog({ loading: false, genitori: [], scelto: null, qual: "GENITORE", motivo: "", err: e.response?.data?.detail || e.message });
                  }
                }}
                disabled={!data?.id}
                data-testid="agev-pdf"
                title={!data?.id ? "Salva la scheda prima di stamparla" : "Stampa modulo agevolazione in PDF"}
                style={{ padding: "8px 16px", background: !data?.id ? "#bdbdbd" : "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: !data?.id ? "not-allowed" : "pointer", borderRadius: 3 }}>
              🖨 Anteprima stampa
            </button>
            <button onClick={async () => {
                  if (!data?.id) { alert("Salva prima la scheda per poterla inviare via mail."); return; }
                  setMailDialog({ loading: true, to: "", subject: "", body: "", sending: false });
                  try {
                    const r = await api.get(`/agevolazioni/${alunnoCod}/${annoScol}/mail-preset`);
                    const p = r.data || {};
                    setMailDialog({
                      loading: false,
                      to: (p.to || []).join(", "),
                      subject: p.subject_default || "",
                      body: p.body_default || "",
                      alunno: p.alunno || null,
                      sending: false,
                    });
                  } catch (e) {
                    setMailDialog({ loading: false, to: "", subject: "", body: "", sending: false,
                                    err: e.response?.data?.detail || e.message });
                  }
                }}
                disabled={!data?.id}
                data-testid="agev-invia-mail"
                title={!data?.id ? "Salva la scheda prima di inviarla" : "Invia scheda Agevolazione via mail (PDF allegato)"}
                style={{ padding: "8px 16px", background: !data?.id ? "#bdbdbd" : "#0d47a1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: !data?.id ? "not-allowed" : "pointer", borderRadius: 3 }}>
              📧 Invia via mail
            </button>
            {isAdmin && data?.id && (
              <button onClick={async () => {
                  const conf = window.prompt(
                    `⚠ ELIMINAZIONE SCHEDA AGEVOLAZIONE\n\nVerrà cancellata definitivamente la scheda dell'anno ${annoScol} per questo alunno.${isApprovata ? "\n\n⚠ ATTENZIONE: la scheda è APPROVATA." : ""}\n\nPer confermare, digita esattamente: ELIMINA`
                  );
                  if (conf !== "ELIMINA") { if (conf !== null) alert("Operazione annullata."); return; }
                  try {
                    await api.delete(`/agevolazioni/${alunnoCod}/${annoScol}`);
                    alert("Scheda eliminata.");
                    onClose?.();
                  } catch (e) {
                    alert("Errore eliminazione: " + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="agev-elimina"
                style={{ padding: "8px 14px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                🗑 Elimina scheda
              </button>
            )}
            {isAdmin && (
              <button onClick={salva} disabled={saving || !data}
                      data-testid="agev-salva"
                      title={isApprovata ? "Salva modifiche (scheda APPROVATA — modifica immediata)" : "Salva la scheda"}
                      style={{ padding: "8px 20px", background: isApprovata ? "#ef6c00" : "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", borderRadius: 3 }}>
                {saving ? "Salvataggio…" : isApprovata ? "💾 Salva (Approvata)" : "💾 Salva scheda"}
              </button>
            )}
          </div>
        </div>
      </div>

      {printDialog && (
        <ScegliGenitoreModal
          dlg={printDialog}
          onCancel={() => setPrintDialog(null)}
          onConferma={async ({ nome_genitore, qual, motivo }) => {
            try {
              const params = new URLSearchParams({
                qual: qual || "GENITORE",
                motivo: motivo || "",
                nome_genitore: nome_genitore || "",
                pag_iscrizione: (data.pagamenti?.iscrizione || "").trim(),
                pag_rette: (data.pagamenti?.rette || "").trim(),
              });
              const r = await api.get(`/agevolazioni/${alunnoCod}/${annoScol}/preview?${params.toString()}`);
              const blob = new Blob([r.data], { type: "text/html" });
              const url = window.URL.createObjectURL(blob);
              setPreviewUrl(url);
              setPrintDialog(null);
            } catch (e) {
              alert("Errore anteprima: " + (e.response?.data?.detail || e.message));
            }
          }}
        />
      )}

      {previewUrl && (
        <PreviewStampa url={previewUrl} onClose={() => {
          window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }} />
      )}

      {mailDialog && (
        <SendMailModal
          dlg={mailDialog}
          onChange={(patch) => setMailDialog((m) => ({ ...m, ...patch }))}
          onClose={() => setMailDialog(null)}
          onSend={async () => {
            const to = (mailDialog.to || "")
              .split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
            if (!to.length) { setMailDialog((m) => ({ ...m, err: "Inserire almeno un destinatario." })); return; }
            if (!(mailDialog.subject || "").trim()) { setMailDialog((m) => ({ ...m, err: "Oggetto obbligatorio." })); return; }
            if (!(mailDialog.body || "").trim()) { setMailDialog((m) => ({ ...m, err: "Corpo email obbligatorio." })); return; }
            setMailDialog((m) => ({ ...m, sending: true, err: null }));
            try {
              const res = await api.post(`/agevolazioni/${alunnoCod}/${annoScol}/send-mail`, {
                to,
                subject: mailDialog.subject,
                body: mailDialog.body,
                pag_iscrizione: (data.pagamenti?.iscrizione || "").trim(),
                pag_rette: (data.pagamenti?.rette || "").trim(),
              });
              const d = res.data || {};
              if (d.ok) {
                setMailDialog((m) => ({
                  ...m, sending: false,
                  ok: `Email inviata a ${d.sent?.length || to.length} destinatari — allegato: ${d.attachment || "PDF"}` +
                      (d.errors?.length ? ` (${d.errors.length} errori: ${d.errors.map((e) => e.email).join(", ")})` : ""),
                  err: null,
                }));
              } else {
                setMailDialog((m) => ({
                  ...m, sending: false,
                  err: "Invio fallito: " + (d.errors?.map((e) => `${e.email}: ${e.error}`).join("; ") || "vedi log"),
                }));
              }
            } catch (e) {
              setMailDialog((m) => ({
                ...m, sending: false,
                err: "Errore invio: " + (e.response?.data?.detail || e.message),
              }));
            }
          }}
        />
      )}
    </div>
  );
}

function ScegliGenitoreModal({ dlg, onCancel, onConferma }) {
  const [scelto, setScelto] = useState(null);
  const [manuale, setManuale] = useState("");
  const [qual, setQual] = useState(dlg?.qual || "GENITORE");
  const [motivo, setMotivo] = useState(dlg?.motivo || "");

  if (dlg.loading) {
    return (
      <div data-testid="agev-genitori-loading" onClick={(e) => e.stopPropagation()} style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "white", padding: 20, borderRadius: 4 }}>Caricamento genitori…</div>
      </div>
    );
  }

  const useManuale = !scelto;
  const nome_genitore = scelto ? scelto.nome_completo : manuale.trim();
  const canConfirm = !!nome_genitore;

  return (
    <div data-testid="agev-scegli-genitore" onClick={(e) => e.stopPropagation()}
         style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "white", width: 520, maxWidth: "100%", borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        <div style={{ background: "#1565c0", color: "white", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 13 }}>🖨 Stampa Agevolazione — seleziona richiedente</span>
          <span style={{ flex: 1 }} />
          <button onClick={onCancel} data-testid="agev-scegli-close"
            style={{ padding: "3px 10px", background: "white", color: "#1565c0", border: "none", fontWeight: 700, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, overflow: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {dlg.err && <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 12 }}>{dlg.err}</div>}

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 6 }}>Scegli il genitore richiedente:</div>
            {dlg.genitori.length === 0 ? (
              <div style={{ fontSize: 12, color: "#888", fontStyle: "italic", padding: 8, border: "1px dashed #ccc" }}>
                Nessun genitore trovato in anagrafica per questo alunno. Inserisci il nome manualmente sotto.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {dlg.genitori.map((g, i) => (
                  <label key={i} data-testid={`agev-gen-radio-${i}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `2px solid ${scelto === g ? "#1565c0" : "#e0e0e0"}`, borderRadius: 3, cursor: "pointer", background: scelto === g ? "#e3f2fd" : "white" }}>
                    <input type="radio" name="genitore-radio" checked={scelto === g}
                      onChange={() => { setScelto(g); setQual(g.qualifica || "GENITORE"); }} />
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{g.nome_completo}</span>
                    <span style={{ fontSize: 11, color: "#1565c0", background: "#e3f2fd", padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>{g.qualifica}</span>
                  </label>
                ))}
                <label data-testid="agev-gen-radio-manual"
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `2px solid ${useManuale ? "#1565c0" : "#e0e0e0"}`, borderRadius: 3, cursor: "pointer", background: useManuale ? "#e3f2fd" : "white" }}>
                  <input type="radio" name="genitore-radio" checked={useManuale} onChange={() => setScelto(null)} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>Inserisci manualmente:</span>
                  <input type="text" value={manuale} onChange={(e) => { setManuale(e.target.value); setScelto(null); }}
                    data-testid="agev-gen-manuale"
                    placeholder="Cognome e nome"
                    style={{ flex: 1, padding: "4px 6px", border: "1px solid #ccc", fontSize: 12 }} />
                </label>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 3 }}>In qualità di:</div>
              <select value={qual} onChange={(e) => setQual(e.target.value)}
                data-testid="agev-scegli-qual"
                style={{ width: "100%", padding: "5px 8px", border: "1px solid #888", fontSize: 12 }}>
                <option value="PADRE">PADRE</option>
                <option value="MADRE">MADRE</option>
                <option value="GENITORE">GENITORE</option>
                <option value="TUTORE">TUTORE</option>
                <option value="NONNO/A">NONNO/A</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 3 }}>Motivo (opzionale):</div>
              <input type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                data-testid="agev-scegli-motivo"
                placeholder="es. ISEE basso, 3 figli a carico…"
                style={{ width: "100%", padding: "5px 8px", border: "1px solid #888", fontSize: 12 }} />
            </div>
          </div>
        </div>
        <div style={{ padding: 10, background: "#fafafa", borderTop: "1px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} data-testid="agev-scegli-annulla"
            style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={() => onConferma({ nome_genitore, qual, motivo })}
            disabled={!canConfirm} data-testid="agev-scegli-conferma"
            style={{ padding: "6px 16px", background: canConfirm ? "#2e7d32" : "#bdbdbd", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: canConfirm ? "pointer" : "not-allowed" }}>
            🖨 Apri anteprima
          </button>
        </div>
      </div>
    </div>
  );
}


function PreviewStampa({ url, onClose }) {
  const iframeRef = useRef(null);
  const stampa = () => {
    try {
      const w = iframeRef.current?.contentWindow;
      if (!w) { alert("Anteprima non pronta, riprova."); return; }
      w.focus();
      w.print();
    } catch (e) {
      alert("Impossibile aprire la stampa: " + e.message);
    }
  };
  return (
    <div data-testid="agev-preview-modal" onClick={(e) => e.stopPropagation()} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#37474f", color: "white", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontWeight: 800, fontSize: 13 }}>🖨 ANTEPRIMA STAMPA — Scheda Agevolazione</span>
        <span style={{ flex: 1 }} />
        <button onClick={stampa} data-testid="agev-preview-print"
                style={{ padding: "6px 16px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
          🖨 Stampa
        </button>
        <button onClick={onClose} data-testid="agev-preview-close"
                style={{ padding: "6px 14px", background: "white", color: "#37474f", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
          ✕ Chiudi
        </button>
      </div>
      <iframe ref={iframeRef} src={url} title="Anteprima Agevolazione"
              style={{ flex: 1, width: "100%", border: "none", background: "white" }} />
    </div>
  );
}

function NumInput({ value, onChange, testid, style, disabled }) {
  return (
    <input
      type="number"
      step="0.01"
      value={value === 0 || value ? value : ""}
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      data-testid={testid}
      disabled={disabled}
      style={{
        padding: "4px 6px",
        border: "1px solid #bdbdbd",
        borderRadius: 2,
        fontSize: 12,
        textAlign: "right",
        fontFamily: "Consolas, monospace",
        fontWeight: 600,
        width: 110,
        boxSizing: "border-box",
        background: disabled ? "#f5f5f5" : "white",
        ...(style || {}),
      }}
    />
  );
}

function btnWorkflow(bg, color) {
  return {
    background: bg, color: color, border: "none",
    padding: "6px 14px", fontSize: 11, fontWeight: 800,
    cursor: "pointer", borderRadius: 3, letterSpacing: 0.3,
  };
}

const tbl = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, letterSpacing: 0.3, border: "1px solid #455a64" };
const thR = { ...th, textAlign: "right" };
const tdLabel = { padding: "8px 10px", borderBottom: "1px solid #e0e0e0", fontWeight: 700, color: "#37474f", verticalAlign: "top", width: 280 };
const tdR = { padding: "6px 10px", borderBottom: "1px solid #e0e0e0", textAlign: "right", verticalAlign: "top" };
const tdAgev = { padding: "8px 10px", borderBottom: "1px solid #e0e0e0", textAlign: "right", fontWeight: 800, color: "#ef6c00", fontFamily: "Consolas, monospace", background: "#fff3e0", width: 150 };
const lbl = { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 4 };


// ============== INVIO MAIL SCHEDA AGEVOLAZIONE (feb 2026) ==============
export function SendMailModal({ dlg, onChange, onClose, onSend }) {
  const [editHtml, setEditHtml] = useState(false);
  if (!dlg) return null;
  if (dlg.loading) {
    return (
      <div data-testid="agev-mail-loading" onClick={(e) => e.stopPropagation()}
           style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "white", padding: 20, borderRadius: 4, fontSize: 13 }}>
          Preparazione compositore email…
        </div>
      </div>
    );
  }
  const alunnoLabel = dlg.alunno?.nome_completo || "";
  const canSend = !dlg.sending && !dlg.ok;
  return (
    <div data-testid="agev-mail-modal" onClick={(e) => e.stopPropagation()}
         style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "white", width: 820, maxWidth: "100%", borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
        <div style={{ background: "#0d47a1", color: "white", padding: "10px 14px", fontSize: 13, fontWeight: 800, letterSpacing: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>📧 INVIA SCHEDA AGEVOLAZIONE {alunnoLabel && `— ${alunnoLabel}`}</span>
          <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 500 }}>Provider: Resend · PDF allegato automatico</span>
        </div>
        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Destinatari */}
          <div>
            <label style={mailLbl}>Destinatari (separati da virgola)</label>
            <input
              type="text"
              value={dlg.to || ""}
              onChange={(e) => onChange({ to: e.target.value })}
              placeholder="genitore1@example.com, genitore2@example.com"
              data-testid="agev-mail-to"
              disabled={!canSend}
              style={mailInput}
            />
            <div style={{ fontSize: 10.5, color: "#666", marginTop: 3 }}>
              Ogni destinatario riceve una copia separata (nessun CC/BCC condiviso).
            </div>
          </div>
          {/* Oggetto */}
          <div>
            <label style={mailLbl}>Oggetto</label>
            <input
              type="text"
              value={dlg.subject || ""}
              onChange={(e) => onChange({ subject: e.target.value })}
              data-testid="agev-mail-subject"
              disabled={!canSend}
              style={mailInput}
            />
          </div>
          {/* Corpo */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <label style={{ ...mailLbl, marginBottom: 0 }}>Testo della mail {editHtml ? "(HTML)" : "(anteprima)"} — firma e disclaimer inclusi</label>
              <button type="button" onClick={() => setEditHtml((v) => !v)} data-testid="agev-mail-toggle-html"
                style={{ background: "transparent", border: "1px solid #90a4ae", borderRadius: 4, fontSize: 11, fontWeight: 700, color: "#37474f", padding: "3px 9px", cursor: "pointer" }}>
                {editHtml ? "👁 Anteprima" : "✏️ Modifica testo"}
              </button>
            </div>
            {editHtml ? (
              <textarea
                value={dlg.body || ""}
                onChange={(e) => onChange({ body: e.target.value })}
                rows={12}
                data-testid="agev-mail-body"
                disabled={!canSend}
                style={{ ...mailInput, fontFamily: "Consolas, monospace", fontSize: 11, lineHeight: 1.45, resize: "vertical", minHeight: 240 }}
              />
            ) : (
              <div data-testid="agev-mail-preview"
                dangerouslySetInnerHTML={{ __html: dlg.body || "<i>(vuoto)</i>" }}
                style={{ border: "1px solid #cfd8dc", borderRadius: 4, padding: "12px 16px", background: "#fcfcfc", fontSize: 13, lineHeight: 1.5, color: "#222", minHeight: 240, maxHeight: 360, overflowY: "auto" }}
              />
            )}
          </div>
          {dlg.err && (
            <div data-testid="agev-mail-err"
                 style={{ background: "#ffebee", color: "#b71c1c", border: "1px solid #ef9a9a", borderRadius: 4, padding: 10, fontSize: 12, fontWeight: 600 }}>
              &#9888; {dlg.err}
            </div>
          )}
          {dlg.ok && (
            <div data-testid="agev-mail-ok"
                 style={{ background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7", borderRadius: 4, padding: 10, fontSize: 12, fontWeight: 700 }}>
              &#10003; {dlg.ok}
            </div>
          )}
        </div>
        <div style={{ padding: 12, borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa" }}>
          <button onClick={onClose} data-testid="agev-mail-close"
                  style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            {dlg.ok ? "Chiudi" : "Annulla"}
          </button>
          {!dlg.ok && (
            <button onClick={onSend} disabled={!canSend}
                    data-testid="agev-mail-send"
                    style={{ padding: "8px 20px", background: canSend ? "#0d47a1" : "#90a4ae", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: canSend ? "pointer" : "not-allowed", borderRadius: 3 }}>
              {dlg.sending ? "Invio in corso…" : "📧 Invia email"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const mailLbl = { fontSize: 11, fontWeight: 700, color: "#37474f", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 4 };
const mailInput = { width: "100%", padding: "8px 10px", border: "1px solid #b0bec5", borderRadius: 3, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", outline: "none" };
